import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppTimeField } from '../../components/AppTimeField';
import { getProjectModuleCapabilities } from '../../config/projectCapabilities';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  addTaskTimeLog,
  deleteTaskTimeLog,
  listTaskTimeLogs,
} from '../../services/projectService';
import type { ProjectAssignee, ProjectMember, TaskTimeLog } from '../../types/project';
import { formatTime, todayBusinessCalendarDate } from '../../utils/dateTime';
import { formatDurationCompact } from '../../utils/projectTaskForm';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  defaultTimeLogEmployeeId,
  employeeInitials,
  fallbackLoggedTime,
  groupTimeLogs,
  logsForDisplay,
  parseClockInput,
  timeLogDurationMinutes,
  timeLogEmployeeOptions,
  timeLogSourceLabel,
} from '../../utils/taskTimeLog';
import { runOperation } from '../../utils/operationFeedback';

type Props = {
  projectId: number;
  taskId: number;
  taskName: string;
  canEdit: boolean;
  existingMinutes?: number;
  employeeId?: number | null;
  employeeName?: string | null;
  members: ProjectMember[];
  assignees: ProjectAssignee[];
  onBack: () => void;
  onChanged: (timeSpentMinutes: number) => void;
};

export function TaskTimeLogView({
  projectId,
  taskId,
  taskName,
  canEdit,
  existingMinutes = 0,
  employeeId,
  employeeName,
  members,
  assignees,
  onBack,
  onChanged,
}: Props) {
  const palette = useAppColors();
  const auth = useAuth();
  // The API always records the session employee; HR Admins / Team Leads
  // (who may have no employee profile) pick one of the task's assignees.
  const selfEmployeeId = auth.user?.employeeId ?? null;
  const canLogForOthers =
    getProjectModuleCapabilities(auth.permissions, auth.roles).scope !== 'MEMBER';
  const employeeOptions = useMemo(
    () =>
      canLogForOthers
        ? timeLogEmployeeOptions({ assignees, members, selfEmployeeId })
        : [],
    [assignees, canLogForOthers, members, selfEmployeeId],
  );
  const [logFor, setLogFor] = useState<number | null>(null);
  const selectedEmployeeId =
    logFor != null && employeeOptions.some(item => item.employeeId === logFor)
      ? logFor
      : defaultTimeLogEmployeeId(employeeOptions, selfEmployeeId);
  const [logs, setLogs] = useState<TaskTimeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState(todayBusinessCalendarDate);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [formError, setFormError] = useState('');
  const [menuId, setMenuId] = useState<number | null>(null);

  const themed = useMemo(
    () => ({
      title: { ...typography.screenTitle, color: palette.textPrimary, fontSize: 24 },
      taskName: { ...typography.caption, color: palette.textSecondary, fontWeight: '600' as const },
      add: { ...typography.body, color: palette.primary, fontWeight: '700' as const },
      day: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
      meta: { ...typography.caption, color: palette.textSecondary },
      name: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      error: { ...typography.caption, color: palette.error },
      empty: { ...typography.body, color: palette.textMuted },
    }),
    [palette],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listTaskTimeLogs(projectId, taskId);
      setLogs(result.logs);
      if (result.logs.length || result.timeSpentMinutes > 0) {
        onChanged(result.timeSpentMinutes || existingMinutes);
      }
      setError('');
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to load the time log.'));
    } finally {
      setLoading(false);
    }
  }, [existingMinutes, projectId, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = groupTimeLogs(
    logsForDisplay(
      logs,
      fallbackLoggedTime({
        taskId,
        minutes: existingMinutes,
        employeeId,
        employeeName,
      }),
    ),
  );

  async function saveEntry() {
    if (!canEdit || busy) return;
    if (!parseClockInput(startTime) || !parseClockInput(endTime)) {
      setFormError('Select a valid start and end time.');
      return;
    }
    if (timeLogDurationMinutes(startTime, endTime) == null) {
      setFormError('End time must be after start time.');
      return;
    }
    if (canLogForOthers && selectedEmployeeId == null) {
      setFormError(
        employeeOptions.length
          ? 'Select the employee this time entry is for.'
          : 'Assign this task to a project member before logging time.',
      );
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      const result = await runOperation('add', () => addTaskTimeLog(projectId, taskId, {
        date,
        startTime,
        endTime,
        source: 'MANUAL',
        ...(canLogForOthers && selectedEmployeeId != null
          ? { employeeId: selectedEmployeeId }
          : {}),
      }));
      setLogs(current => (result.log ? [result.log, ...current] : current));
      onChanged(result.timeSpentMinutes);
      setAdding(false);
      setStartTime('');
      setEndTime('');
    } catch (caught) {
      setFormError(projectErrorMessage(caught, 'Unable to add the time log.'));
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete(log: TaskTimeLog) {
    Alert.alert('Delete time log', 'Remove this time entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: () => {
          void (async () => {
            setBusy(true);
            try {
              const result = await runOperation(
                'delete',
                () => deleteTaskTimeLog(projectId, taskId, log.timeLogId),
                {
                  failureDetail: caught =>
                    projectErrorMessage(caught, 'The time log could not be deleted.'),
                },
              );
              setLogs(current => current.filter(item => item.timeLogId !== log.timeLogId));
              onChanged(result.timeSpentMinutes);
              setMenuId(null);
            } catch {
              // runOperation already showed the "Delete failed" popup.
            } finally {
              setBusy(false);
            }
          })();
        },
      },
    ]);
  }

  return (
    <View style={styles.body}>
      <View style={styles.header}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={8}
        >
          <Text style={themed.taskName}>← {taskName}</Text>
        </Pressable>
        {canEdit ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Add entry"
            onPress={() => {
              setAdding(current => !current);
              setFormError('');
            }}
            hitSlop={8}
          >
            <Text style={themed.add}>+ ADD ENTRY</Text>
          </Pressable>
        ) : null}
      </View>
      <Text style={themed.title}>Time log</Text>

      {adding ? (
        <View style={styles.form}>
          {canLogForOthers ? (
            <View style={styles.employeeField}>
              <Text style={themed.meta}>Employee</Text>
              {employeeOptions.length ? (
                <View style={styles.employeeRow}>
                  {employeeOptions.map(option => {
                    const selected = option.employeeId === selectedEmployeeId;
                    return (
                      <Pressable
                        key={option.employeeId}
                        accessibilityRole="button"
                        accessibilityLabel={`Log time for ${option.employeeName}`}
                        accessibilityState={{ selected }}
                        onPress={() => setLogFor(option.employeeId)}
                        style={[
                          styles.employeeChip,
                          {
                            borderColor: selected ? palette.primary : palette.border,
                            backgroundColor: selected ? palette.primary : palette.surface,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            themed.meta,
                            { color: selected ? palette.onPrimary : palette.textPrimary },
                          ]}
                        >
                          {option.employeeId === selfEmployeeId
                            ? `${option.employeeName} (you)`
                            : option.employeeName}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : (
                <Text style={themed.error}>
                  Assign this task to a project member before logging time.
                </Text>
              )}
            </View>
          ) : null}
          <AppDateField label="Date" value={date} onChange={setDate} />
          <View style={styles.timeRow}>
            <View style={styles.flex}>
              <AppTimeField
                label="Start time"
                value={startTime}
                onChange={setStartTime}
              />
            </View>
            <View style={styles.flex}>
              <AppTimeField
                label="End time"
                value={endTime}
                onChange={setEndTime}
              />
            </View>
          </View>
          {formError ? <Text style={themed.error}>{formError}</Text> : null}
          <AppButton title="Save entry" onPress={() => void saveEntry()} loading={busy} />
        </View>
      ) : null}

      {loading ? <Text style={themed.empty}>Loading time log…</Text> : null}
      {error ? <Text style={themed.error}>{error}</Text> : null}
      {!loading && !groups.length && !error ? (
        <Text style={themed.empty}>No time has been logged yet.</Text>
      ) : null}

      {groups.map(group => (
        <View key={group.date} style={styles.day}>
          <View style={styles.dayHeader}>
            <Text style={themed.day}>{group.label}</Text>
            <Text style={themed.day}>{group.totalLabel}</Text>
          </View>
          {group.entries.map(log => {
            const badge = timeLogSourceLabel(log.source);
            return (
              <View key={log.timeLogId} style={styles.row}>
                <View
                  style={[styles.avatar, { backgroundColor: palette.primary }]}
                >
                  <Text style={[styles.avatarText, { color: palette.onPrimary }]}>
                    {employeeInitials(log.employeeName)}
                  </Text>
                </View>
                <View style={styles.flex}>
                  <Text style={themed.name}>{log.employeeName}</Text>
                  <Text style={themed.meta}>
                    {formatTime(log.startedAt)} - {formatTime(log.endedAt)}
                  </Text>
                </View>
                <Text style={themed.name}>
                  {formatDurationCompact(log.durationMinutes) || '0m'}
                </Text>
                <View
                  style={[
                    styles.badge,
                    {
                      backgroundColor:
                        badge !== 'M' ? palette.success : palette.border,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      { color: badge !== 'M' ? palette.onSuccess : palette.textSecondary },
                    ]}
                  >
                    {badge}
                  </Text>
                </View>
                {canEdit &&
                log.timeLogId > 0 &&
                (canLogForOthers || log.employeeId === selfEmployeeId) ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={`More actions for ${log.employeeName} time log`}
                    onPress={() =>
                      setMenuId(current =>
                        current === log.timeLogId ? null : log.timeLogId,
                      )
                    }
                    hitSlop={8}
                  >
                    <Text style={themed.meta}>⋮</Text>
                  </Pressable>
                ) : null}
                {menuId === log.timeLogId ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Delete time log"
                    onPress={() => confirmDelete(log)}
                    style={[
                      styles.menu,
                      { backgroundColor: palette.surface, borderColor: palette.border },
                    ]}
                  >
                    <Text style={[themed.name, { color: palette.error }]}>Delete</Text>
                  </Pressable>
                ) : null}
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  body: { gap: spacing.md },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  form: { gap: spacing.sm },
  employeeField: { gap: spacing.xs },
  employeeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  employeeChip: {
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  timeRow: { flexDirection: 'row', gap: spacing.sm },
  flex: { flex: 1 },
  day: { gap: spacing.sm, marginTop: spacing.sm },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    position: 'relative',
    minHeight: 44,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: { ...typography.caption, fontWeight: '700' },
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: { fontSize: 11, fontWeight: '700' },
  menu: {
    position: 'absolute',
    right: 0,
    top: 36,
    zIndex: 4,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  empty: { marginTop: spacing.md },
});
